import React, {ReactNode, createContext, useContext, useState} from "react"

import type {DraftReplyBlocked, DraftReplyDraft} from "@indebted/api"

type DraftReplyResult =
	| {
			kind: "ok"
			draft: DraftReplyDraft
			omittedFields: string[]
			contactWarning?: string
			complianceFallback: boolean
	  }
	| {kind: "blocked"; reason: DraftReplyBlocked["reason"]}

type PickableDocument = {
	noteId: string
	uploadId: string
	fileName: string
}

// The reply an agent is composing for one inbound message. Switching tabs
// unmounts the panel, so this is what lets a draft outlive it.
type DraftReplySession = {
	guidance: string
	result: DraftReplyResult | null
	body: string
	pickedRecipient: string
	selectedDocument: PickableDocument | null
	sent: boolean
	// Work started by one mount that another may come back to, and how it ended.
	pending: "drafting" | "sending" | null
	generationFailed: boolean
	sendError: string | null
}

// A message nobody has drafted for reads as an empty session.
const EMPTY_SESSION: DraftReplySession = {
	guidance: "",
	result: null,
	body: "",
	pickedRecipient: "",
	selectedDocument: null,
	sent: false,
	pending: null,
	generationFailed: false,
	sendError: null,
}

function createDraftReplySessions() {
	const byMessage = new Map<string, DraftReplySession>()
	const listeners = new Map<string, Set<() => void>>()
	return {
		read: (key: string) => byMessage.get(key) ?? EMPTY_SESSION,
		write: (key: string, session: DraftReplySession) => {
			byMessage.set(key, session)
			listeners.get(key)?.forEach((listener) => listener())
		},
		subscribe: (key: string, listener: () => void) => {
			const forKey = listeners.get(key) ?? new Set<() => void>()
			forKey.add(listener)
			listeners.set(key, forKey)
			return () => {
				forKey.delete(listener)
			}
		},
	}
}

const DraftReplySessionsContext = createContext<ReturnType<typeof createDraftReplySessions> | null>(null)

// Mounted once for the app's lifetime, so sessions survive every route change
// short of a reload. Keyed by the message the reply answers.
function DraftReplySessionsProvider({children}: {children: ReactNode}) {
	const [sessions] = useState(createDraftReplySessions)
	return <DraftReplySessionsContext.Provider value={sessions}>{children}</DraftReplySessionsContext.Provider>
}

function useDraftReplySessions() {
	const sessions = useContext(DraftReplySessionsContext)
	if (!sessions) {
		throw new Error("useDraftReplySessions must be used within DraftReplySessionsProvider")
	}
	return sessions
}

export {DraftReplySessionsProvider, useDraftReplySessions}
export type {DraftReplySession, PickableDocument}
